// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import React, {useState} from 'react';
import get from 'lodash/get';
import {Button} from '@app/components/inputs/buttons/Button';
import {
    BLOCK_KEYS,
    BLOCK_TYPES,
    CX_BLOCK_TYPES,
    renderTitle,
} from '@app/constants';
import {KareMultiselectCheckbox} from '@app/common';

interface CXCreationModalProps {
    addCX?: any;
    closeModal?: any;
    type?: string;
    currentCX?: {
        metricID: string;
        question: string;
        labelNegative: string;
        labelNeutral: string;
        labelPositive: string;
        thankYouMsg: string;
        followUp: boolean;
        followUpQuestion: string;
    };
}

/*
 * Creates a functional component that provides the customization of
 * Customer Experience blocks (NPS, CSAT & CES) through a modal.
 */
export const CXCreationModal = (props: CXCreationModalProps) => {
    const mapCurrentCX = (CXObject) => {
        return CXObject
            ? {
                  type: props.type,
                  metricID: CXObject.metric_id,
                  question: CXObject.question,
                  labelNegative: CXObject.label_negative,
                  labelNeutral:
                      CXObject.label_neutral && !!CXObject.label_neutral.length
                          ? CXObject.label_neutral
                          : undefined,
                  labelPositive: CXObject.label_positive,
                  thankYouMsg: CXObject.completion_message,
                  followUp: !!CXObject.follow_up_question,
                  followUpQuestion: CXObject.follow_up_question,
              }
            : undefined;
    };

    // Receives the current CX block object from the parent component (ResponseFormComponent).
    const currentCX = mapCurrentCX(get(props.currentCX, ['cx_question_block']));

    /*
     * Binds the set function and value of the input fields for
     * each property of the currentNPS object.
     */
    const [metricID, setMetricID] = useState(
        currentCX ? currentCX.metricID : '',
    );
    const [question, setQuestion] = useState(
        currentCX ? currentCX.question : '',
    );
    const [labelNegative, setlabelNegative] = useState(
        currentCX ? currentCX.labelNegative : '',
    );

    const [labelNeutral, setlabelNeutral] = useState(
        currentCX ? currentCX.labelNeutral : '',
    );

    const [labelPositive, setlabelPositive] = useState(
        currentCX ? currentCX.labelPositive : '',
    );
    const [thankYouMsg, setThankYouMsg] = useState(
        currentCX ? currentCX.thankYouMsg : '',
    );
    const [followUp, setFollowUp] = useState(
        currentCX ? currentCX.followUp : false,
    );
    const [followUpQuestion, setFollowUpQuestion] = useState(
        currentCX ? currentCX.followUpQuestion : '',
    );

    const [errors, setErrors] = useState([]);
    const validateForm = () => {
        const errors = [];
        if (!metricID) {
            errors.push('metricID');
        }
        if (!question) {
            errors.push('question');
        }
        if (!thankYouMsg) {
            errors.push('thankYouMsg');
        }
        if (followUp && !followUpQuestion) {
            errors.push('followUpQuestion');
        }
        return errors;
    };
    // Adds this CX Block to the response object.
    const addCX = () => {
        const errors = validateForm();
        if (errors && errors.length > 0) {
            setErrors(errors);
            return;
        }
        /*  Executes the addCX function from the parent component (ResponseFormComponent)
         * which inserts or updates the array of blocks.
         */
        props.addCX({
            [BLOCK_KEYS.CX_QUESTION_BLOCK]: {
                type: props.type,
                metric_id: metricID,
                question,
                label_negative: labelNegative,
                label_neutral:
                    props.type === CX_BLOCK_TYPES.CSAT
                        ? labelNeutral
                        : undefined,
                label_positive: labelPositive,
                completion_message: thankYouMsg,
                followUp,
                follow_up_question: followUpQuestion,
            },
            type: BLOCK_TYPES.CX_QUESTION,
        });
        props.closeModal();
    };
    return (
        <div className="cx-creation-modal">
            <h2>Add {renderTitle(props.type)}</h2>
            <div className="cx-creation-modal-content">
                <div className="cx-creation-modal-wrapper-input">
                    <span className="cx-creation-modal-h3">Metric ID</span>
                    <input
                        onChange={(e) => {
                            setMetricID(e.target.value);
                        }}
                        value={metricID}
                    />
                    {errors && errors.includes('metricID') && (
                        <span className="cx-creation-modal-error">
                            Missing Metric Identifier
                        </span>
                    )}
                </div>
                <div className="cx-creation-modal-wrapper-input">
                    <span className="cx-creation-modal-h3">Question</span>
                    <textarea
                        style={{
                            maxHeight: '60px',
                        }}
                        onChange={(e) => {
                            setQuestion(e.target.value);
                        }}
                        value={question}
                    />
                    {errors && errors.includes('question') && (
                        <span className="cx-creation-modal-error">
                            Missing question text
                        </span>
                    )}
                </div>
                <div className="cx-creation-modal-wrapper-input">
                    <span>Minimum Label</span>
                    <input
                        onChange={(e) => {
                            setlabelNegative(e.target.value);
                        }}
                        value={labelNegative}
                    />
                    {errors && errors.includes('labelNegative') && (
                        <span className="cx-creation-modal-error">
                            Missing minimum label text
                        </span>
                    )}
                </div>
                {props.type === CX_BLOCK_TYPES.CSAT ? (
                    <div className="cx-creation-modal-wrapper-input">
                        <span>Neutral Label</span>
                        <input
                            onChange={(e) => {
                                setlabelNeutral(e.target.value);
                            }}
                            value={labelNeutral}
                        />
                        {errors && errors.includes('labelNeutral') && (
                            <span className="cx-creation-modal-error">
                                Missing neutral label text
                            </span>
                        )}
                    </div>
                ) : null}
                <div className="cx-creation-modal-wrapper-input">
                    <span>Maximum Label</span>
                    <input
                        onChange={(e) => {
                            setlabelPositive(e.target.value);
                        }}
                        value={labelPositive}
                    />
                    {errors && errors.includes('labelPositive') && (
                        <span className="cx-creation-modal-error">
                            Missing maximum label text
                        </span>
                    )}
                </div>
                <div className="cx-creation-modal-wrapper-input">
                    <span>Thank you message</span>
                    <input
                        onChange={(e) => {
                            setThankYouMsg(e.target.value);
                        }}
                        value={thankYouMsg}
                    />
                    {errors && errors.includes('thankYouMsg') && (
                        <span className="cx-creation-modal-error">
                            Missing Thank you message
                        </span>
                    )}
                </div>
                <div className="cx-creation-modal-wrapper-input">
                    <KareMultiselectCheckbox
                        onClick={() => {
                            const hasFollowUpQuestion = !followUp;
                            setFollowUp(hasFollowUpQuestion);
                            !hasFollowUpQuestion
                                ? setFollowUpQuestion('')
                                : null;
                        }}
                        isChecked={followUp}
                        label={'Ask follow up question'}
                    />
                </div>
                {
                    // Shows or hides the follow up input depending on the followUp checkbox being checked.
                    followUp ? (
                        <div className="cx-creation-modal-wrapper-input">
                            <span>Follow up question</span>
                            <input
                                onChange={(e) => {
                                    setFollowUpQuestion(e.target.value);
                                }}
                                value={followUpQuestion}
                            />
                            {errors && errors.includes('followUpQuestion') && (
                                <span className="cx-creation-modal-error">
                                    Missing Follow up question
                                </span>
                            )}
                        </div>
                    ) : null
                }
            </div>
            <div className="cx-creation-modal-wrapper-buttons">
                <Button text="ADD" type="confirm" onClick={addCX} />
                <Button
                    text="CANCEL"
                    type="cancel"
                    onClick={props.closeModal}
                />
            </div>
        </div>
    );
};
